import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Group, DefaultGridsterOptions, ScriptTestMode, Identifier, IGridsterOptions } from '../../../models';
import { IRootScope } from 'IRootScope';
import { IIdentifierVmService, IIdentifierStateService, IRequestService, IDateRangeService, IValuesService, IScriptEditionService } from '../../../services';
import { groupScriptModeChanged, refreshClick } from '../../../common';

class ReconciliationStateController implements angular.IComponentController {
    consoleCtrl: any;
    flipEquation = false;
    flipMeasured = true;
    formController: angular.IFormController;
    gridsterOptions: IGridsterOptions;
    groupGuid: string;
    groupLoadedPromise: angular.IPromise<any>;
    identifiers: any[];
    isCodeEditorDisabled: boolean;
    isLoading: boolean;
    loadingMessage: string;
    reconciliationResults: any;
    resultsWidgetItem: { isMaximizable: boolean; isMaximized: boolean; isDetachable: boolean; };
    scriptTestMode: ScriptTestMode;
    selectedGroup: Group;

    static $inject = [
        '$rootScope',
        '$scope',
        '$q',
        'identifierVmService',
        'identifierStateService',
        'requestService',
        'dateRangeService',
        'valuesService',
        'scriptEditionService'
    ];

    constructor(private _$rootScope: IRootScope,
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _identifierVmService: IIdentifierVmService,
        private _identifierStateService: IIdentifierStateService,
        private _requestService: IRequestService,
        private _dateRangeService: IDateRangeService,
        private _valuesService: IValuesService,
        private _scriptEditionService: IScriptEditionService) {
    }

    $onInit() {
        this.groupGuid = this.selectedGroup.guid;
        this.gridsterOptions = DefaultGridsterOptions;

        this.identifiers = [];
        this.resultsWidgetItem = {
            isMaximizable: true,
            isMaximized: false,
            isDetachable: false
        };

        if (this.selectedGroup.script) {
            this.selectedGroup.script = this.selectedGroup.script.trim();
        }
        if (this._dateRangeService.initialized()) {
            this.loadReconciliationResults(this.getDateAccordingMode());

        }
        this.isLoading = true;
        const groupDefer = this._$q.defer();

        this._$scope.$on(groupScriptModeChanged, (event, data) => {
            const oldMode = this.scriptTestMode;
            this.scriptTestMode = data;
            if (oldMode !== data) {
                this.loadReconciliationResults(this.getDateAccordingMode());
                this.refresh(this.selectedGroup.identifierCollection, groupDefer);
            }
        });

        this._$scope.$watch(() => this.selectedGroup, (newValue) => {
            if (newValue) {
                this.refresh(this.selectedGroup.identifierCollection, groupDefer);
            }
        }, true);

        this._$scope.$on(refreshClick, () => {
            this.groupLoadedPromise.then(() => {
                this.loadReconciliationResults(this.getDateAccordingMode());
                this.refresh(this.selectedGroup.identifierCollection, groupDefer);
            });
        });

        this._$scope.$watch(() => this.formController, (newValue) => {
            if (newValue) {
                this._scriptEditionService.scriptFormController(this.formController);
            }
        });

        this.groupLoadedPromise = groupDefer.promise;
    }

    isReadOnly() {
        return !this._$rootScope.isEditMode;
    }

    private getDateAccordingMode(): string {
        return this.scriptTestMode === ScriptTestMode.BeginDateValue
            ? this._dateRangeService.beginDate() as string
            : this._dateRangeService.endDate() as string;
    }

    private loadReconciliationResults(date: string) {
        this.isLoading = true;
        this._requestService.getAtV1(`reconciliationResults`, { date: date, guid: this.groupGuid })
            .then(data => this.reconciliationResults = data)
            .catch((reason) => console.error(reason))
            .finally(() => this.isLoading = false);
    }

    private refresh(identifiers: Identifier[], defer: any) {
        this.isLoading = true;

        this._identifierVmService
            .getViewModelsFromApiFromGuidList(identifiers.map(i => i.guid))
            .then(data => {
                this.identifiers = this._identifierStateService.mergeViewModels(identifiers, data);
            })
            .then(() => {
                return this._valuesService.getValuesAndStatuses(
                    this.identifiers.map(i => i.guid),
                    this.getDateAccordingMode()
                );
            })
            .then((data: any[]) => {
                this.identifiers.forEach(i => {
                    const found = _.find(data, d => d.guid === i.guid);
                    if (found) {
                        i.value = found.value;
                        i.status = found.status;
                    }
                });
            })
            .finally(() => {
                defer.resolve();
                this.isLoading = false;
            });
    }

    cancel(form: angular.IFormController): void {
        form.$cancel();
        form.$setPristine();
        if (this._identifierStateService.formController) {
            this._identifierStateService.formController.$rollbackViewValue();
        }
    }

    submit(form: angular.IFormController): void {
        form.$submit();
    }

    watchKeyPress($event, item) {
        if ($event.keyCode === 27) {
            if (item.isMaximized && !item.isDisabledMaximized) {
                item.isMaximized = false;
                $event.preventDefault();
            }
        }
    }
}

export const wtReconciliationState: ComponentDefinition = {
    componentId: 'wtReconciliationState',
    controller: ReconciliationStateController,
    template: require('./reconciliationState.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=?',
        gridsterOptions: '<',
        isCodeEditorDisabled: '<',
        scriptTestMode: '=',
        selectedGroup: '<',
    }
};